import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Trash2, Settings as SettingsIcon, ChevronDown, RefreshCw, Camera, Info, Loader2 } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useBusinessId } from '../../hooks/useBusinessId';
import { toast } from 'sonner';
import SelectServiceModal from '../../components/manage/SelectServiceModal';
import ModifierModal from '../../components/ModifierModal';
import { Service, ServiceCategory } from '../../lib/services';
import { useBusiness } from '../../contexts/BusinessContext';
import { Switch } from '@headlessui/react';
import BusinessLogoUpload from '../../components/BusinessLogoUpload';
import {
  buildPublicOnlineBookingUrl,
  getOnlineBookingPublicOriginFromPortalRow,
} from '../../lib/onlineBookingUrls';
import BrandedOnlineBookingPortalHostSection from '../../components/marketing/BrandedOnlineBookingPortalHostSection';
import { getServiceModifiers, upsertServiceModifiers, ServiceModifiers } from '../../lib/serviceTitleTags';

interface SubCategory {
  id: string;
  title: string;
  services: Array<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  }>;
  created_at: string;
  updated_at: string;
}

interface Category {
  id: string;
  category_title: string;
  sub_categories?: SubCategory[];
  /** Tile image shown on the public booking category step. */
  image_url?: string | null;
}

/** Shown from the info icon beside the category image uploads. */
const CATEGORY_IMAGE_HINT =
  'Category images show on the public booking page tiles. Optimal size: square, 600×600 px or larger (JPG or PNG, under 5 MB). Images are cropped to a square, so keep the subject centered.';

interface OnlineBookingPortal {
  id: string;
  business_id: string;
  public_slug: string | null;
  display_name: string;
  is_default: boolean;
  sort_order: number;
  assigned_asset_id: string | null;
  custom_hostname?: string | null;
  custom_host_verified_at?: string | null;
  custom_host_verification_token?: string | null;
}

interface OnlineBookingsProps {
  /**
   * Slim mode for the setup wizard: shows ONLY the category sidebar + flow
   * editor. Hides the portal switcher, enable toggle, URLs, and page
   * customization so new users aren't overwhelmed.
   */
  embedded?: boolean;
}

export default function OnlineBookings({ embedded = false }: OnlineBookingsProps = {}) {
  const navigate = useNavigate();
  const supabase = useSupabase();
  const businessId = useBusinessId();
  const { selectedBusiness, updateBusiness } = useBusiness();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [categoryTitle, setCategoryTitle] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubCategoryModalOpen, setIsSubCategoryModalOpen] = useState(false);
  const [subCategoryTitle, setSubCategoryTitle] = useState("");
  const [isAddingToSubCategory, setIsAddingToSubCategory] = useState(false);
  const [isAssignServicesModalOpen, setIsAssignServicesModalOpen] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const [onlineBookingPortals, setOnlineBookingPortals] = useState<OnlineBookingPortal[]>([]);
  const [selectedPortalId, setSelectedPortalId] = useState<string | null>(null);
  const [isAddPortalModalOpen, setIsAddPortalModalOpen] = useState(false);
  const [newPortalSlug, setNewPortalSlug] = useState('');
  const [newPortalDisplayName, setNewPortalDisplayName] = useState('');
  const [isCreatingPortal, setIsCreatingPortal] = useState(false);
  const [businessAssets, setBusinessAssets] = useState<{ id: string; name: string }[]>([]);
  const [isSavingPortalAsset, setIsSavingPortalAsset] = useState(false);
  
  // New state variables for color picker
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [navbarColor, setNavbarColor] = useState('#000000'); // Default black color
  const [logoInvert, setLogoInvert] = useState(false); // Logo inversion for dark backgrounds
  

  
  // Buffer time state - start with 0 until loaded from database

  // Popup settings state
  const [popupSettings, setPopupSettings] = useState({
    popup_enabled: false,
    popup_delay_seconds: 10,
    popup_duration_seconds: 5,
    popup_text: 'Welcome! Book your appointment today and get 10% off your first service.',
    popup_button_text: 'Book Now',
    popup_button_action: 'url' as 'url' | 'lead_form',
    popup_button_url: '',
    popup_send_email: false,
    popup_email_template_id: '',
    call_button_enabled: false,
    booking_discount_percentage: 0,
    google_reviews_badge_enabled: false,
    google_reviews_rating: 5,
    google_reviews_count: null as number | null,
    google_reviews_url: ''
  });
  const [popupSettingsLoaded, setPopupSettingsLoaded] = useState<boolean>(false);

  // Custom thank you URL state
  const [customThankYouUrl, setCustomThankYouUrl] = useState<string>('');

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState<Array<{id: string; name: string}>>([]);

  // New state variables for service info modal
  const [isServiceInfoModalOpen, setIsServiceInfoModalOpen] = useState(false);
  const [selectedServiceInfo, setSelectedServiceInfo] = useState<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  } | null>(null);

  // New state for category editing view
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState<Category | null>(null);
  
  // Delete category confirmation modal state
  const [isDeleteCategoryModalOpen, setIsDeleteCategoryModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; title: string } | null>(null);
  
  // Service tag state
  const [isServiceTagModalOpen, setIsServiceTagModalOpen] = useState(false);
  const [selectedServiceForTag, setSelectedServiceForTag] = useState<{ service: Service; priceOption: any } | null>(null);
  const [serviceTagText, setServiceTagText] = useState('');
  const [serviceTagEnabled, setServiceTagEnabled] = useState(false);
  const [serviceMeterEnabled, setServiceMeterEnabled] = useState(false);
  const [serviceMeterTitle, setServiceMeterTitle] = useState('');
  const [serviceMeterRating, setServiceMeterRating] = useState<number>(0);
  const [serviceTags, setServiceTags] = useState<Map<string, ServiceModifiers>>(new Map());

  // Color picker modal state for consistency with LeadForms
  const [colorPickerModal, setColorPickerModal] = useState<{isOpen: boolean, type: 'navbar' | null, currentColor: string}>({
    isOpen: false,
    type: null,
    currentColor: '#000000'
  });

  // Modifier modal state
  const [isModifierModalOpen, setIsModifierModalOpen] = useState(false);
  const [selectedServiceForModifier, setSelectedServiceForModifier] = useState<{
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  } | null>(null);

  // Functions for category editing view
  const handleCategorySelect = (category: Category) => {
    setSelectedCategoryForEdit(category);
    setSelectedCategoryId(category.id);
  };

  const handleBackToList = () => {
    setSelectedCategoryForEdit(null);
  };

  // Modifier modal functions
  const handleOpenModifierModal = (service: {
    service: Service;
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  }) => {
    setSelectedServiceForModifier(service);
    setIsModifierModalOpen(true);
  };

  const handleCloseModifierModal = () => {
    setIsModifierModalOpen(false);
    setSelectedServiceForModifier(null);
  };

  // Color picker modal functions (matching LeadForms pattern)
  const openColorPicker = (type: 'navbar', currentColor: string) => {
    setColorPickerModal({
      isOpen: true,
      type,
      currentColor
    });
  };

  const closeColorPicker = () => {
    setColorPickerModal({
      isOpen: false,
      type: null,
      currentColor: '#000000'
    });
  };

  const handleColorSelect = (color: string) => {
    if (!colorPickerModal.type) return;

    if (colorPickerModal.type === 'navbar') {
      setNavbarColor(color);
      handleSaveNavbarColor();
    }
    
    closeColorPicker();
  };

  // Predefined color palette (matching LeadForms)
  const colorPalette = [
    '#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#f3f4f6', '#ffffff',
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981',
    '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
    '#ec4899', '#f43f5e', '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d',
    '#16a34a', '#059669', '#0891b2', '#0284c7', '#2563eb', '#4f46e5', '#7c3aed',
    '#9333ea', '#c026d3', '#db2777', '#e11d48'
  ];

    // Helper function to update both categories and selectedCategoryForEdit
  const updateCategoriesAndSelected = (updatedCategories: Category[]) => {
    setCategories(updatedCategories);
    
    // Update selectedCategoryForEdit if it's currently selected
    if (selectedCategoryForEdit) {
      const updatedCategory = updatedCategories.find(cat => cat.id === selectedCategoryForEdit.id);
      if (updatedCategory) {
        setSelectedCategoryForEdit(updatedCategory);
      }
    }
  };

  const handleToggleOnlineBookings = async (enabled: boolean) => {
    if (!selectedBusiness) return;

    try {
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        allow_online_bookings: enabled
      };

      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });

      toast.success(`Online bookings ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating online bookings status:', error);
      toast.error('Failed to update online bookings status');
    }
  };

  const createDefaultPortalAndSettings = async (): Promise<OnlineBookingPortal | null> => {
    if (!supabase || !businessId) return null;
    const { data: portal, error: pe } = await supabase
      .from('online_booking_portals')
      .insert({
        business_id: businessId,
        display_name: 'Primary online booking',
        is_default: true,
        public_slug: null,
        sort_order: 0,
      })
      .select(
        'id, business_id, public_slug, display_name, is_default, sort_order, custom_hostname, custom_host_verified_at, custom_host_verification_token',
      )
      .single();
    if (pe || !portal) {
      console.error('Error creating default portal:', pe);
      return null;
    }
    const { error: se } = await supabase.from('online_booking_settings').insert({
      business_id: businessId,
      portal_id: portal.id,
      buffer_time: 0,
    });
    if (se) console.error('Error creating portal settings:', se);
    return { ...portal, assigned_asset_id: null } as OnlineBookingPortal;
  };

  /** Loads portals; retries without assigned_asset_id if that column is not migrated yet. */
  const fetchPortalList = async (): Promise<OnlineBookingPortal[]> => {
    if (!supabase || !businessId) return [];
    const baseSelect = (columns: string) =>
      supabase
        .from('online_booking_portals')
        .select(columns)
        .eq('business_id', businessId)
        .order('sort_order', { ascending: true });

    const colsWithHost =
      'id, business_id, public_slug, display_name, is_default, sort_order, assigned_asset_id, custom_hostname, custom_host_verified_at, custom_host_verification_token';
    let res = await baseSelect(colsWithHost);
    if (res.error) {
      res = await baseSelect('id, business_id, public_slug, display_name, is_default, sort_order, assigned_asset_id');
      if (res.error) {
        res = await baseSelect('id, business_id, public_slug, display_name, is_default, sort_order');
        if (res.error) {
          console.error('Error loading portals:', res.error);
          toast.error('Failed to load online booking pages');
          return [];
        }
      }
    }
    return ((res.data || []) as Record<string, unknown>[]).map((p) => ({
      ...p,
      assigned_asset_id: (p.assigned_asset_id as string | null | undefined) ?? null,
      custom_hostname: (p.custom_hostname as string | null | undefined) ?? null,
      custom_host_verified_at: (p.custom_host_verified_at as string | null | undefined) ?? null,
      custom_host_verification_token: (p.custom_host_verification_token as string | null | undefined) ?? null,
    })) as OnlineBookingPortal[];
  };

  const loadPortals = async () => {
    if (!supabase || !businessId) return;
    try {
      let list = await fetchPortalList();
      if (list.length === 0) {
        const created = await createDefaultPortalAndSettings();
        if (created) {
          list = [created];
        } else {
          list = await fetchPortalList();
        }
      }
      setOnlineBookingPortals(list);
      setSelectedPortalId((prev) => {
        if (prev && list.some((p) => p.id === prev)) return prev;
        const def = list.find((p) => p.is_default);
        return def?.id ?? list[0]?.id ?? null;
      });
    } catch (e) {
      console.error('loadPortals failed:', e);
      toast.error('Failed to load online booking pages');
    }
  };

  useEffect(() => {
    if (businessId) {
      void loadPortals();
    }
  }, [businessId]);

  useEffect(() => {
    if (!supabase || !businessId) {
      setBusinessAssets([]);
      return;
    }
    void supabase
      .from('assets')
      .select('id, name')
      .eq('business_id', businessId)
      .order('name', { ascending: true })
      .then(({ data, error }) => {
        if (error) {
          console.error('Error loading assets:', error);
          return;
        }
        setBusinessAssets(data || []);
      });
  }, [supabase, businessId]);

  const handlePortalAssignedAssetChange = async (assetId: string | '') => {
    if (!supabase || !selectedPortalId) return;
    const nextId = assetId || null;
    setIsSavingPortalAsset(true);
    try {
      const { error } = await supabase
        .from('online_booking_portals')
        .update({ assigned_asset_id: nextId, updated_at: new Date().toISOString() })
        .eq('id', selectedPortalId);
      if (error) throw error;
      setOnlineBookingPortals((prev) =>
        prev.map((p) => (p.id === selectedPortalId ? { ...p, assigned_asset_id: nextId } : p))
      );
      toast.success(nextId ? 'Bookings on this page now use the selected asset only.' : 'Bookings on this page use customer address again.');
    } catch (e: unknown) {
      console.error(e);
      const code =
        e && typeof e === 'object' && 'code' in e ? String((e as { code?: string }).code) : '';
      const message =
        e && typeof e === 'object' && 'message' in e ? String((e as { message?: string }).message) : '';
      const missingColumn =
        code === 'PGRST204' ||
        /assigned_asset_id/i.test(message) ||
        /schema cache/i.test(message);
      if (missingColumn) {
        toast.error(
          'Your Supabase project is missing column assigned_asset_id on online_booking_portals. Run migration 20260507120000_online_booking_portal_assigned_asset.sql in the SQL Editor, then retry after ~1 minute.'
        );
      } else {
        toast.error('Failed to update asset setting');
      }
    } finally {
      setIsSavingPortalAsset(false);
    }
  };

  // Load navbar color and logo invert from business settings
  useEffect(() => {
    if (selectedBusiness?.booking_settings?.navbar_color) {
      setNavbarColor(selectedBusiness.booking_settings.navbar_color);
    }
    if (selectedBusiness?.booking_settings?.logo_invert !== undefined) {
      setLogoInvert(selectedBusiness.booking_settings.logo_invert);
    }
  }, [selectedBusiness]);



  // Load email templates when business changes
  useEffect(() => {
    if (businessId) {
      fetchEmailTemplates();
    }
  }, [businessId]);

  const fetchEmailTemplates = async () => {
    if (!supabase || !businessId) return;
    
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('id, name')
        .eq('business_id', businessId)
        .eq('one_off', false)
        .order('name');
      
      if (error) {
        console.error('Error fetching email templates:', error);
        return;
      }
      
      setEmailTemplates(data || []);
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const fetchBufferTime = async () => {
    if (!supabase || !businessId || !selectedPortalId) return;
    
    console.log(`[BUFFER TIME] Fetching buffer time and popup settings for portal ${selectedPortalId}`);
    
    try {
      const { data, error } = await supabase
        .from('online_booking_settings')
        .select('popup_enabled, popup_delay_seconds, popup_duration_seconds, popup_text, popup_button_text, popup_button_action, popup_button_url, popup_send_email, popup_email_template_id, call_button_enabled, custom_thank_you_url, booking_discount_percentage, google_reviews_badge_enabled, google_reviews_rating, google_reviews_count, google_reviews_url')
        .eq('portal_id', selectedPortalId)
        .single();
      
      console.log('[BUFFER TIME] Fetch result:', data, error);
      
      if (error) {
        if (error.code === 'PGRST116') {
          // Record not found - explicitly set defaults and mark as loaded
          console.log('[BUFFER TIME] No settings found in database, setting defaults');
          setPopupSettingsLoaded(true);
        } else {
          console.error('[BUFFER TIME] Error fetching settings:', error);
          // On other errors, still mark as loaded but keep defaults
          setPopupSettingsLoaded(true);
        }
        return;
      }
      
      // Set popup settings
      setPopupSettings({
        popup_enabled: data?.popup_enabled || false,
        popup_delay_seconds: data?.popup_delay_seconds || 10,
        popup_duration_seconds: data?.popup_duration_seconds || 5,
        popup_text: data?.popup_text || 'Welcome! Book your appointment today and get 10% off your first service.',
        popup_button_text: data?.popup_button_text || 'Book Now',
        popup_button_action: (data?.popup_button_action as 'url' | 'lead_form') || 'url',
        popup_button_url: data?.popup_button_url || '',
        popup_send_email: data?.popup_send_email || false,
        popup_email_template_id: data?.popup_email_template_id || '',
        call_button_enabled: data?.call_button_enabled || false,
        booking_discount_percentage: data?.booking_discount_percentage || 0,
        google_reviews_badge_enabled: data?.google_reviews_badge_enabled || false,
        google_reviews_rating: data?.google_reviews_rating ?? 5,
        google_reviews_count: data?.google_reviews_count ?? null,
        google_reviews_url: data?.google_reviews_url || ''
      });
      
      // Set custom thank you URL
      setCustomThankYouUrl(data?.custom_thank_you_url || '');
      
      setPopupSettingsLoaded(true);
    } catch (error) {
      console.error('[BUFFER TIME] Error fetching settings:', error);
      setPopupSettingsLoaded(true);
    }
  };

  // Custom thank you URL handler
  const handleCustomThankYouUrlChange = async (url: string) => {
    if (!supabase || !businessId || !selectedPortalId) return;
    
    try {
      // Update UI immediately
      setCustomThankYouUrl(url);
      console.log(`[THANK YOU URL] Setting custom thank you URL for business ${businessId}`);
      
      const { error } = await supabase
        .from('online_booking_settings')
        .upsert(
          { 
            business_id: businessId,
            portal_id: selectedPortalId,
            custom_thank_you_url: url || null // Store null if empty string
          },
          { 
            onConflict: 'portal_id'
          }
        );
      
      if (error) {
        throw error;
      }
      
      toast.success(url ? 'Custom thank you URL saved' : 'Custom thank you URL removed');
    } catch (error) {
      console.error('[THANK YOU URL] Error saving custom thank you URL:', error);
      toast.error('Failed to save custom thank you URL. Please try again.');
    }
  };

  // Popup settings handlers
  const handlePopupToggle = async (enabled: boolean) => {
    if (!supabase || !businessId || !selectedPortalId) return;
    
    try {
      const newSettings = { ...popupSettings, popup_enabled: enabled };
      setPopupSettings(newSettings);
      
      const { error } = await supabase
        .from('online_booking_settings')
        .upsert(
          { 
            business_id: businessId,
            portal_id: selectedPortalId,
            popup_enabled: enabled,
            popup_delay_seconds: newSettings.popup_delay_seconds,
            popup_duration_seconds: newSettings.popup_duration_seconds,
            popup_text: newSettings.popup_text,
            popup_button_text: newSettings.popup_button_text,
            popup_button_action: newSettings.popup_button_action,
            popup_button_url: newSettings.popup_button_url,
            popup_send_email: newSettings.popup_send_email,
            popup_email_template_id: newSettings.popup_email_template_id,
            call_button_enabled: newSettings.call_button_enabled
          },
          { 
            onConflict: 'portal_id'
          }
        );
      
      if (error) throw error;
      
      toast.success(`Popup window ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error saving popup settings:', error);
      toast.error('Failed to save popup settings. Please try again.');
    }
  };

  const handlePopupSettingChange = async (setting: string, value: number | string | boolean) => {
    if (!supabase || !businessId || !selectedPortalId) return;
    
    try {
      // Convert string booleans to actual booleans for specific settings
      let processedValue: any = value;
      if (setting === 'popup_send_email' || setting === 'call_button_enabled' || setting === 'google_reviews_badge_enabled') {
        if (typeof value === 'boolean') {
          processedValue = value;
        } else if (typeof value === 'string') {
          processedValue = value === 'true';
        } else {
          processedValue = false;
        }
      }
      
      const newSettings = { ...popupSettings, [setting]: processedValue };
      setPopupSettings(newSettings);
      
      const { error } = await supabase
        .from('online_booking_settings')
        .upsert(
          { 
            business_id: businessId,
            portal_id: selectedPortalId,
            popup_enabled: newSettings.popup_enabled,
            popup_delay_seconds: newSettings.popup_delay_seconds,
            popup_duration_seconds: newSettings.popup_duration_seconds,
            popup_text: newSettings.popup_text,
            popup_button_text: newSettings.popup_button_text,
            popup_button_action: newSettings.popup_button_action,
            popup_button_url: newSettings.popup_button_url || null,
            popup_send_email: newSettings.popup_send_email,
            popup_email_template_id: newSettings.popup_email_template_id || null,
            call_button_enabled: newSettings.call_button_enabled,
            booking_discount_percentage: newSettings.booking_discount_percentage,
            google_reviews_badge_enabled: newSettings.google_reviews_badge_enabled,
            google_reviews_rating: newSettings.google_reviews_rating,
            google_reviews_count: newSettings.google_reviews_count,
            google_reviews_url: newSettings.google_reviews_url || null
          },
          { 
            onConflict: 'portal_id'
          }
        );
      
      if (error) throw error;
      
      toast.success(
        setting === 'booking_discount_percentage'
          ? 'Booking discount updated'
          : setting.startsWith('google_reviews')
            ? 'Google reviews badge updated'
            : 'Popup settings updated'
      );
    } catch (error) {
      console.error('Error saving popup settings:', error);
      toast.error('Failed to save settings. Please try again.');
    }
  };

  // Re-pull every assigned service from the live catalog: updates renamed /
  // repriced services and removes ones that were deleted from Services.
  const [isSyncingServices, setIsSyncingServices] = useState(false);
  const handleSyncWithServices = async () => {
    if (!businessId || !selectedPortalId || isSyncingServices) return;
    setIsSyncingServices(true);
    try {
      const { syncOnlineBookingWithServices } = await import('../../lib/onlineBookingSync');
      const result = await syncOnlineBookingWithServices(businessId, selectedPortalId);
      await fetchCategories();

      if (result.updatedServices === 0 && result.removedServices === 0) {
        toast.success('Already in sync — no changes needed');
      } else {
        const parts: string[] = [];
        if (result.updatedServices > 0) parts.push(`${result.updatedServices} service${result.updatedServices === 1 ? '' : 's'} updated`);
        if (result.removedServices > 0) parts.push(`${result.removedServices} removed (deleted from Services)`);
        toast.success(`Synced with Services: ${parts.join(', ')}`);
      }
    } catch (error) {
      console.error('Error syncing online booking with services:', error);
      toast.error('Failed to sync with Services. Please try again.');
    } finally {
      setIsSyncingServices(false);
    }
  };

  // Category tile images — one shared file input; the clicked category id is
  // remembered so the picked file lands on the right row.
  const categoryImageInputRef = useRef<HTMLInputElement | null>(null);
  const categoryImageTargetRef = useRef<string | null>(null);
  const [uploadingImageCategoryId, setUploadingImageCategoryId] = useState<string | null>(null);

  const pickCategoryImage = (categoryId: string) => {
    categoryImageTargetRef.current = categoryId;
    categoryImageInputRef.current?.click();
  };

  const applyCategoryImage = async (categoryId: string, imageUrl: string | null) => {
    const { error } = await supabase
      .from('online_booking_flow')
      .update({ image_url: imageUrl })
      .eq('id', categoryId);
    if (error) throw error;
    setCategories(prev => prev.map(c => (c.id === categoryId ? { ...c, image_url: imageUrl } : c)));
  };

  const handleCategoryImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-picking the same file
    const categoryId = categoryImageTargetRef.current;
    if (!file || !categoryId || !businessId) return;
    try {
      setUploadingImageCategoryId(categoryId);
      const ext = file.name.split('.').pop() || 'jpg';
      // Timestamped path → replacing an image gets a fresh URL (no stale cache).
      const path = `online-booking-categories/${businessId}/${categoryId}-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
      await applyCategoryImage(categoryId, publicUrl);
      toast.success('Category image updated');
    } catch (err) {
      console.error('Category image upload failed:', err);
      toast.error('Image upload failed — please try again.');
    } finally {
      setUploadingImageCategoryId(null);
    }
  };

  const handleRemoveCategoryImage = async (categoryId: string) => {
    try {
      await applyCategoryImage(categoryId, null);
      toast.success('Category image removed');
    } catch (err) {
      console.error('Category image remove failed:', err);
      toast.error('Failed to remove image — please try again.');
    }
  };

  const fetchCategories = async () => {
    if (!businessId || !selectedPortalId) return;
    try {
      const { data, error } = await supabase
        .from('online_booking_flow')
        .select('id, category_title, sub_categories, image_url')
        .eq('business_id', businessId)
        .eq('portal_id', selectedPortalId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setCategories(data || []);
      
      // Update selectedCategoryForEdit if it's currently selected
      if (selectedCategoryForEdit) {
        const updatedCategory = data?.find(cat => cat.id === selectedCategoryForEdit.id);
        if (updatedCategory) {
          setSelectedCategoryForEdit(updatedCategory);
        }
      }
      
      // Fetch service tags for all services in categories
      if (data && data.length > 0) {
        fetchServiceTags(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  useEffect(() => {
    if (!businessId || !selectedPortalId) return;
    setSelectedCategoryForEdit(null);
    void fetchCategories();
    void fetchBufferTime();
  }, [businessId, selectedPortalId]);

  const fetchServiceTags = async (categoriesData: Category[]) => {
    if (!businessId) return;
    
    try {
      // Collect all service IDs from all categories
      const serviceIds: string[] = [];
      categoriesData.forEach(category => {
        category.sub_categories?.forEach(subCategory => {
          subCategory.services?.forEach(serviceItem => {
            if (serviceItem.service?.id) {
              serviceIds.push(serviceItem.service.id);
            }
          });
        });
      });
      
      if (serviceIds.length === 0) return;
      
      // Fetch all service tags
      const tagsMap = new Map<string, ServiceModifiers>();
      for (const serviceId of serviceIds) {
        try {
          const tag = await getServiceModifiers(serviceId);
          if (tag) {
            tagsMap.set(serviceId, tag);
          }
        } catch (error) {
          // Service might not have a tag, which is fine
          console.log(`No tag found for service ${serviceId}`);
        }
      }
      
      setServiceTags(tagsMap);
    } catch (error) {
      console.error('Error fetching service tags:', error);
    }
  };

  const handleOpenTagModal = (service: Service, priceOption: any) => {
    setSelectedServiceForTag({ service, priceOption });
    const existingTag = serviceTags.get(service.id);
    setServiceTagText(existingTag?.title_tag_text || '');
    setServiceTagEnabled(existingTag?.title_tag_enabled || false);
    setServiceMeterEnabled(existingTag?.service_meter_enabled || false);
    setServiceMeterTitle(existingTag?.meter_title || '');
    setServiceMeterRating(existingTag?.meter_rating || 0);
    setIsServiceTagModalOpen(true);
  };

  const handleSaveServiceTag = async () => {
    if (!selectedServiceForTag || !businessId) return;
    
    try {
      const tag = await upsertServiceModifiers(selectedServiceForTag.service.id, {
        business_id: businessId,
        service_id: selectedServiceForTag.service.id,
        title_tag_enabled: serviceTagEnabled,
        title_tag_text: serviceTagEnabled ? serviceTagText.trim() : null,
        service_meter_enabled: serviceMeterEnabled,
        meter_title: serviceMeterEnabled ? serviceMeterTitle.trim() : null,
        meter_rating: serviceMeterEnabled ? serviceMeterRating : null
      });
      
      // Update local state
      const newTags = new Map(serviceTags);
      newTags.set(selectedServiceForTag.service.id, tag);
      setServiceTags(newTags);
      
      setIsServiceTagModalOpen(false);
      setSelectedServiceForTag(null);
      setServiceTagText('');
      setServiceTagEnabled(false);
      setServiceMeterEnabled(false);
      setServiceMeterTitle('');
      setServiceMeterRating(0);
      
      toast.success('Service tag and meter saved successfully');
    } catch (error) {
      console.error('Error saving service tag:', error);
      toast.error('Failed to save service tag');
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryTitle.trim() || !businessId || !selectedPortalId) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('online_booking_flow')
        .insert([{
          business_id: businessId,
          portal_id: selectedPortalId,
          category_title: categoryTitle.trim()
        }])
        .select()
        .single();

      if (error) throw error;

      setCategories([...categories, data]);
      setIsModalOpen(false);
      setCategoryTitle('');
      toast.success('Category created successfully');
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to create category');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlusClick = (categoryId: string, isSubCategoryPlus: boolean = false) => {
    setSelectedCategoryId(categoryId);
    setIsAddingToSubCategory(isSubCategoryPlus);
    setIsOptionsModalOpen(true);
  };

  const handleAddSubCategory = async () => {
    if (!subCategoryTitle.trim() || !selectedCategoryId) {
      toast.error("Please enter a title for the sub-category");
      return;
    }

    try {
      // Get the current category to access its existing sub-categories
      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', selectedCategoryId)
        .single();

      if (fetchError) throw fetchError;

      // Create new sub-category object
      const newSubCategory = {
        id: crypto.randomUUID(),
        title: subCategoryTitle.trim(),
        services: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Combine existing sub-categories with new one
      const updatedSubCategories = [
        ...(currentCategory?.sub_categories || []),
        newSubCategory
      ];

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', selectedCategoryId);

      if (updateError) throw updateError;

      // Refresh categories to get latest data
      await fetchCategories();

      setSubCategoryTitle("");
      setIsSubCategoryModalOpen(false);
      toast.success("Sub-category added successfully");
    } catch (error) {
      console.error('Error adding sub-category:', error);
      toast.error('Failed to add sub-category');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      const { error } = await supabase
        .from('online_booking_flow')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      // Refresh categories to get latest data
      await fetchCategories();
      
      // Clear selected category if it was deleted
      if (selectedCategoryForEdit?.id === categoryId) {
        setSelectedCategoryForEdit(null);
      }
      
      toast.success("Category deleted successfully");
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleConfirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    
    await handleDeleteCategory(categoryToDelete.id);
    setIsDeleteCategoryModalOpen(false);
    setCategoryToDelete(null);
  };

  const handleDeleteSubCategory = async (subCategory: SubCategory) => {
    if (!selectedCategoryId) return;
    
    try {
      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', selectedCategoryId)
        .single();

      if (fetchError) throw fetchError;

      const updatedSubCategories = currentCategory?.sub_categories?.filter((sc: SubCategory) => sc.id !== subCategory.id) || [];

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', selectedCategoryId);

      if (updateError) throw updateError;

      // Update local UI state
      const updatedCategories = categories.map(category => {
        if (category.id === selectedCategoryId) {
          const updatedCategory = {
            ...category,
            sub_categories: updatedSubCategories
          };
          
          // Also update selectedCategoryForEdit if it's the same category
          if (selectedCategoryForEdit && selectedCategoryForEdit.id === selectedCategoryId) {
            setSelectedCategoryForEdit(updatedCategory);
          }
          
          return updatedCategory;
        }
        return category;
      });

      setCategories(updatedCategories);
      toast.success("Sub-category deleted successfully");
    } catch (error) {
      console.error('Error deleting sub-category:', error);
      toast.error('Failed to delete sub-category');
    }
  };

  const handleServiceAdd = async (service: Service, priceOption: any) => {
    if (!selectedSubCategory || !selectedCategoryId || !businessId) return;

    try {
      // Get the current category to access its existing sub-categories
      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', selectedCategoryId)
        .single();

      if (fetchError) throw fetchError;

      // Check if service is already assigned
      const isServiceAssigned = currentCategory?.sub_categories?.some((subCat: SubCategory) => 
        subCat.id === selectedSubCategory.id && 
        subCat.services?.some((s: { service: Service; priceOption: { id: string; name: string; price: number; duration_minutes: number; } }) => 
          s.service.id === service.id && s.priceOption.id === priceOption.id
        )
      );

      if (isServiceAssigned) {
        toast.error('This service is already assigned to this sub-category');
        return;
      }

      // Update the sub-categories array with the new service
      const updatedSubCategories = currentCategory?.sub_categories?.map((subCat: SubCategory) => {
        if (subCat.id === selectedSubCategory.id) {
          return {
            ...subCat,
            services: [
              ...(subCat.services || []),
              {
                service: service,
                priceOption: priceOption
              }
            ]
          };
        }
        return subCat;
      });

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', selectedCategoryId);

      if (updateError) throw updateError;

      // Update local UI state
      const updatedCategories = categories.map(category => {
        if (category.id === selectedCategoryId) {
          const updatedCategory = {
            ...category,
            sub_categories: updatedSubCategories
          };
          
          // Also update selectedCategoryForEdit if it's the same category
          if (selectedCategoryForEdit && selectedCategoryForEdit.id === selectedCategoryId) {
            setSelectedCategoryForEdit(updatedCategory);
          }
          
          return updatedCategory;
        }
        return category;
      });

      setCategories(updatedCategories);
      
      // Fetch tag for the newly added service
      try {
        const tag = await getServiceModifiers(service.id);
        if (tag) {
          const newTags = new Map(serviceTags);
          newTags.set(service.id, tag);
          setServiceTags(newTags);
        }
      } catch (error) {
        // Service might not have a tag, which is fine
        console.log(`No tag found for service ${service.id}`);
      }
      
      toast.success('Service assigned successfully');
    } catch (error) {
      console.error('Error assigning service:', error);
      toast.error('Failed to assign service');
    }
  };

  const handleDeleteService = async (subCategory: SubCategory, service: Service) => {
    try {
      // Find the category that contains this sub-category
      const category = categories.find(cat => 
        cat.sub_categories?.some(sc => sc.id === subCategory.id)
      );

      if (!category) {
        throw new Error('Category not found');
      }

      const { data: currentCategory, error: fetchError } = await supabase
        .from('online_booking_flow')
        .select('sub_categories')
        .eq('id', category.id)
        .single();

      if (fetchError) throw fetchError;

      const updatedSubCategories = currentCategory?.sub_categories?.map((sc: SubCategory) => {
        if (sc.id === subCategory.id) {
          return {
            ...sc,
            services: (sc.services || []).filter((s: { service: Service; priceOption: { id: string; name: string; price: number; duration_minutes: number; } }) => s.service.id !== service.id)
          };
        }
        return sc;
      });

      // Update the database
      const { error: updateError } = await supabase
        .from('online_booking_flow')
        .update({
          sub_categories: updatedSubCategories
        })
        .eq('id', category.id);

      if (updateError) throw updateError;

      // Update local UI state
      const updatedCategories = categories.map(cat => {
        if (cat.id === category.id) {
          const updatedCategory = {
            ...cat,
            sub_categories: updatedSubCategories
          };
          
          // Also update selectedCategoryForEdit if it's the same category
          if (selectedCategoryForEdit && selectedCategoryForEdit.id === category.id) {
            setSelectedCategoryForEdit(updatedCategory);
          }
          
          return updatedCategory;
        }
        return cat;
      });

      setCategories(updatedCategories);
      toast.success('Service removed successfully');
    } catch (error) {
      console.error('Error removing service:', error);
      toast.error('Failed to remove service');
    }
  };

  const handleSubCategoryDrop = (subCat: SubCategory) => {
    // ... existing code ...
  };

  const handleServiceDrop = (service: { service: Service; priceOption: { id: string; name: string; price: number; duration_minutes: number; } }) => {
    // ... existing code ...
  };

  const handleSubCategoryDragStart = (subCat: SubCategory) => {
    // ... existing code ...
  };

  const handleCopyUrl = async () => {
    if (!bookingPublicUrl) return;
    
          const url = bookingPublicUrl;
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      toast.error('Failed to copy URL');
    }
  };

  const handleSaveNavbarColor = async () => {
    if (!selectedBusiness) return;
    
    try {
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        navbar_color: navbarColor
      };
      
      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });
      
      setIsColorPickerOpen(false);
      toast.success('Navigation bar color updated');
    } catch (error) {
      console.error('Error updating navbar color:', error);
      toast.error('Failed to update navigation bar color');
    }
  };

  const handleSaveLogoInvert = async (newLogoInvert?: boolean) => {
    if (!selectedBusiness) {
      console.log('No selected business, cannot save logo invert');
      return;
    }
    
    const logoInvertValue = newLogoInvert !== undefined ? newLogoInvert : logoInvert;
    console.log('Saving logo invert with value:', logoInvertValue);
    
    try {
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        logo_invert: logoInvertValue
      };
      
      console.log('Updated settings:', updatedSettings);
      
      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });
      
      console.log('Logo invert saved successfully');
      toast.success('Logo style updated');
    } catch (error) {
      console.error('Error updating logo style:', error);
      toast.error('Failed to update logo style');
    }
  };

  const handleCreateAdditionalPortal = async () => {
    if (!supabase || !businessId || !selectedBusiness?.slug) return;
    const slug = newPortalSlug.trim().toLowerCase();
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug) || slug.length < 2) {
      toast.error('Use 2+ characters: lowercase letters, numbers, or hyphens (e.g. commercial or auto-care).');
      return;
    }
    if (['book', 'booking-success', 'que', 'store', 'gift-cards', 'intake'].includes(slug)) {
      toast.error('That URL suffix is reserved.');
      return;
    }
    setIsCreatingPortal(true);
    try {
      const nextOrder = onlineBookingPortals.reduce((m, p) => Math.max(m, p.sort_order), 0) + 1;
      const { data: portal, error: pe } = await supabase
        .from('online_booking_portals')
        .insert({
          business_id: businessId,
          display_name: newPortalDisplayName.trim() || slug,
          is_default: false,
          public_slug: slug,
          sort_order: nextOrder,
        })
        .select(
          'id, business_id, public_slug, display_name, is_default, sort_order, custom_hostname, custom_host_verified_at, custom_host_verification_token',
        )
        .single();
      if (pe || !portal) throw pe || new Error('Failed to create portal');
      const { error: se } = await supabase.from('online_booking_settings').insert({
        business_id: businessId,
        portal_id: portal.id,
        buffer_time: 0,
      });
      if (se) throw se;
      toast.success('Online booking page created');
      setNewPortalSlug('');
      setNewPortalDisplayName('');
      setIsAddPortalModalOpen(false);
      await loadPortals();
      setSelectedPortalId(portal.id);
    } catch (e: unknown) {
      console.error(e);
      const code =
        e && typeof e === 'object' && 'code' in e ? String((e as { code?: string }).code) : '';
      toast.error(
        code === '23505' ? 'That URL suffix is already in use.' : 'Failed to create booking page.'
      );
    } finally {
      setIsCreatingPortal(false);
    }
  };

  const selectedPortal = onlineBookingPortals.find((p) => p.id === selectedPortalId) ?? null;
  const bookingPublicUrl =
    selectedBusiness?.slug && selectedPortal
      ? buildPublicOnlineBookingUrl(
          getOnlineBookingPublicOriginFromPortalRow(selectedPortal),
          selectedBusiness.slug,
          selectedPortal,
        )
      : '';

  return (
    <div className={embedded ? 'relative z-0' : 'min-h-screen bg-gray-50 relative z-0'} style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'}}>
      {/* Main Content */}
      <main className={embedded ? 'w-full relative z-0' : 'w-full max-w-none mx-auto px-2 sm:px-4 md:px-6 py-3 sm:py-6 md:py-8 relative z-0'}>
        {/* Floating Container */}
        <div className={embedded ? 'relative z-0' : 'bg-white rounded-[12px] sm:rounded-[16px] shadow-sm border border-gray-200/60 p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 md:mb-8 relative z-0'} style={embedded ? undefined : {boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)'}}>


          {/* Categories Section - Redesigned with Sidebar */}
          <div className="bg-white rounded-[10px] sm:rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] mb-4 sm:mb-6 overflow-hidden">
            <div className="flex flex-col lg:flex-row min-h-[500px] sm:min-h-[600px]">
              {/* Left Sidebar - Categories List */}
              <div className="w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-gray-200/60 bg-gray-50/50 flex flex-col">
                {/* Sidebar Header */}
                <div className="p-2.5 sm:p-4 border-b border-gray-200/60 bg-white">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <h3 className="text-[15px] sm:text-[17px] font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                        Categories
                      </h3>
                      {/* Image size guidance for the per-category uploads below */}
                      <button
                        type="button"
                        onClick={() => toast.info(CATEGORY_IMAGE_HINT, { duration: 8000 })}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                        title="Category image size guide"
                        aria-label="Category image size guide"
                      >
                        <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    </span>
                    <button
                      onClick={() => setIsModalOpen(true)}
                      className="p-1.5 sm:p-2 bg-blue-600 text-white rounded-[8px] active:bg-blue-700 transition-all duration-150 active:scale-[0.97] touch-manipulation"
                      style={{ 
                        minHeight: '28px',
                        minWidth: '28px',
                        WebkitTapHighlightColor: 'transparent'
                      }}
                      title="Create Category"
                    >
                      <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </button>
                  </div>
                </div>
                
                {/* Categories List */}
                <div className="flex-1 overflow-y-auto p-2 sm:p-3" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {categories.length === 0 ? (
                    <div className="text-center py-6 sm:py-8">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-[8px] sm:rounded-[10px] bg-gray-100 flex items-center justify-center mx-auto mb-2 sm:mb-3">
                        <Plus className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                      </div>
                      <p className="text-[12px] sm:text-[13px] text-gray-600 mb-3 sm:mb-4" style={{ letterSpacing: '-0.01em' }}>
                        No categories yet
                      </p>
                      <button
                        onClick={() => setIsModalOpen(true)}
                        className="inline-flex items-center justify-center px-4 sm:px-5 py-2 sm:py-2.5 text-[14px] sm:text-[15px] font-semibold text-white bg-blue-600 rounded-[8px] sm:rounded-[10px] active:bg-blue-700 transition-all duration-150 active:scale-[0.97] touch-manipulation shadow-sm"
                        style={{ 
                          minHeight: '40px',
                          WebkitTapHighlightColor: 'transparent',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                        Create Category
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-1.5 sm:space-y-2">
                      {categories.map(category => (
                        <div
                          key={category.id}
                          onClick={() => handleCategorySelect(category)}
                          className={`group relative p-2.5 sm:p-3 rounded-[8px] sm:rounded-[10px] border transition-all duration-150 touch-manipulation cursor-pointer ${
                            selectedCategoryForEdit?.id === category.id
                              ? 'bg-blue-50 border-blue-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                              : 'bg-white border-gray-200/60 hover:bg-gray-50 active:bg-gray-100'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCategoryToDelete({ id: category.id, title: category.category_title });
                              setIsDeleteCategoryModalOpen(true);
                            }}
                            className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 p-1 sm:p-1.5 bg-red-600 text-white active:bg-red-700 rounded-[6px] sm:rounded-[8px] transition-all duration-150 active:scale-[0.95] touch-manipulation opacity-0 group-hover:opacity-100 z-10 shadow-sm"
                            style={{ 
                              minHeight: '24px',
                              minWidth: '24px',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                            title="Delete category"
                          >
                            <Trash2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                          </button>
                          <div className="flex items-center gap-2.5">
                            {/* Tile image for the public booking page — tap to upload/change */}
                            <span className="relative flex-shrink-0">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  pickCategoryImage(category.id);
                                }}
                                disabled={uploadingImageCategoryId === category.id}
                                className="h-10 w-10 rounded-[8px] overflow-hidden border border-gray-200/60 bg-gray-100 flex items-center justify-center hover:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                                title={category.image_url ? 'Change category image' : 'Add category image'}
                              >
                                {uploadingImageCategoryId === category.id ? (
                                  <Loader2 className="h-4 w-4 text-gray-500 animate-spin" />
                                ) : category.image_url ? (
                                  <img src={category.image_url} alt="" className="h-full w-full object-cover" />
                                ) : (
                                  <Camera className="h-4 w-4 text-gray-400" />
                                )}
                              </button>
                              {category.image_url && uploadingImageCategoryId !== category.id && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveCategoryImage(category.id);
                                  }}
                                  className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-gray-700 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                                  title="Remove image"
                                >
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              )}
                            </span>
                            <div className="min-w-0">
                              <h5 className="text-[14px] sm:text-[15px] font-semibold text-gray-900 mb-0.5 truncate" style={{ letterSpacing: '-0.01em' }}>
                                {category.category_title}
                              </h5>
                              <p className="text-[11px] sm:text-[12px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                                {category.sub_categories?.length || 0} {category.sub_categories?.length === 1 ? 'subcategory' : 'subcategories'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Shared picker for category tile images */}
                  <input
                    ref={categoryImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCategoryImageUpload}
                    className="hidden"
                  />
                </div>
              </div>
              
              {/* Right Main Area - Flow Editor */}
              <div className="flex-1 bg-white flex flex-col">
                {/* Embedded mode: just the live page link so users can see what they're building */}
                {embedded && bookingPublicUrl && (
                  <div className="border-b border-gray-100 bg-gray-50/90 px-3 sm:px-4 py-2.5 flex items-center gap-2 min-w-0">
                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide shrink-0">
                      Your booking page
                    </span>
                    <a
                      href={bookingPublicUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 min-w-0 truncate text-[12px] font-medium text-blue-600 hover:text-blue-700 hover:underline underline-offset-2"
                      title="Open your booking page in a new tab"
                    >
                      {bookingPublicUrl}
                    </a>
                  </div>
                )}
                {!embedded && onlineBookingPortals.length > 0 && (
                  <div className="border-b border-gray-100 bg-gray-50/90 px-3 sm:px-4 lg:px-6 py-2.5">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Booking page</span>
                        {onlineBookingPortals.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setSelectedPortalId(p.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                              selectedPortalId === p.id
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {p.display_name}
                            {!p.is_default && p.public_slug ? (
                              <span className="opacity-80 font-normal"> · /{p.public_slug}</span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsAddPortalModalOpen(true)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add another online booking
                      </button>
                    </div>
                    {selectedPortal && (
                      <div className="mt-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 pt-2.5 border-t border-gray-200/80">
                        <label
                          htmlFor="portal-assigned-asset"
                          className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide shrink-0"
                        >
                          Book with asset
                        </label>
                        <div className="flex flex-1 flex-col xs:flex-row xs:items-center gap-2 min-w-0">
                          <select
                            id="portal-assigned-asset"
                            disabled={isSavingPortalAsset}
                            value={selectedPortal.assigned_asset_id ?? ''}
                            onChange={(e) => void handlePortalAssignedAssetChange(e.target.value as string | '')}
                            className="flex-1 min-w-0 text-xs sm:text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60"
                          >
                            <option value="">Customer address (service areas)</option>
                            {businessAssets.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.name}
                              </option>
                            ))}
                          </select>
                          <p className="text-[11px] text-gray-500 leading-snug xs:max-w-md">
                            When an asset is selected, customers skip the address step. Time slots follow that asset’s mobile availability and existing bookings.
                          </p>
                        </div>
                      </div>
                    )}
                    {businessId && selectedPortal && (
                      <BrandedOnlineBookingPortalHostSection
                        businessId={businessId}
                        portalId={selectedPortal.id}
                        onUpdated={() => void loadPortals()}
                      />
                    )}
                  </div>
                )}
                {/* Online Booking Set Up Header */}
          {!embedded && selectedBusiness && (
                  <div className="bg-white border-b border-gray-200/60">
                    <div className="px-3 sm:px-4 lg:px-6 py-1.5 sm:py-2">
                      <div className="flex items-center justify-between mb-1 sm:mb-1.5">
                        <h4 className="text-[14px] sm:text-[15px] font-semibold text-gray-900 truncate flex-1 mr-2" style={{ letterSpacing: '-0.01em' }}>
                        Online Booking Set Up
                    </h4>
                        
                        {/* Toggle Switch */}
                        <div className="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0">
                          <span className="text-[11px] sm:text-[12px] text-gray-600 hidden xs:inline" style={{ letterSpacing: '-0.01em' }}>
                            {selectedBusiness.booking_settings?.allow_online_bookings ? 'Enabled' : 'Disabled'}
                          </span>
                          <Switch
                            checked={selectedBusiness.booking_settings?.allow_online_bookings || false}
                            onChange={handleToggleOnlineBookings}
                            className={`${
                              selectedBusiness.booking_settings?.allow_online_bookings ? 'bg-blue-600' : 'bg-gray-200'
                            } relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                          >
                            <span
                              className={`${
                                selectedBusiness.booking_settings?.allow_online_bookings ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                              } inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </Switch>
                        </div>
                  </div>
                  
                      {/* Action Buttons and URL */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2">
                      {/* Preview and Copy Link */}
                      <div className="flex items-center space-x-1.5 sm:space-x-2">
                      <button 
                        onClick={() => {
                          if (bookingPublicUrl) {
                            window.open(bookingPublicUrl, '_blank');
                          }
                        }}
                          className="px-2.5 sm:px-3 py-1.5 text-[12px] sm:text-[13px] font-semibold text-blue-600 bg-blue-50 rounded-[8px] hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.97] touch-manipulation"
                          style={{ 
                            WebkitTapHighlightColor: 'transparent',
                            letterSpacing: '-0.01em'
                          }}
                      >
                        Preview
                      </button>
                      <button 
                        onClick={() => {
                          if (bookingPublicUrl) {
                            navigator.clipboard.writeText(bookingPublicUrl);
                            toast.success('Booking URL copied to clipboard!');
                          }
                        }}
                          className="px-2.5 sm:px-3 py-1.5 text-[12px] sm:text-[13px] font-semibold text-blue-600 bg-blue-50 rounded-[8px] hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.97] touch-manipulation"
                          style={{ 
                            WebkitTapHighlightColor: 'transparent',
                            letterSpacing: '-0.01em'
                          }}
                      >
                        Copy Link
                      </button>
                      <button
                        onClick={handleSyncWithServices}
                        disabled={isSyncingServices}
                        className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 text-[12px] sm:text-[13px] font-semibold text-blue-600 bg-blue-50 rounded-[8px] hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.97] touch-manipulation disabled:opacity-60"
                        style={{
                          WebkitTapHighlightColor: 'transparent',
                          letterSpacing: '-0.01em'
                        }}
                        title="Refresh all assigned services from the Services app (updates changes, removes deleted services)"
                      >
                        <RefreshCw className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${isSyncingServices ? 'animate-spin' : ''}`} />
                        {isSyncingServices ? 'Syncing…' : 'Sync with Services'}
                      </button>
                    </div>
                    
                        {/* URL Dropdown */}
                {bookingPublicUrl && (
                          <details className="group">
                            <summary 
                              className="flex items-center justify-between cursor-pointer list-none touch-manipulation py-1 active:bg-gray-50 rounded-[8px] px-1.5 -mx-1.5 transition-colors [&::-webkit-details-marker]:hidden [&::marker]:hidden" 
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <span className="text-[12px] sm:text-[13px] text-gray-600 font-medium" style={{ letterSpacing: '-0.01em' }}>Booking URL</span>
                              <ChevronDown className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 transition-transform group-open:rotate-180 ml-1.5 sm:ml-2" />
                            </summary>
                            <div className="mt-1.5 sm:mt-2 pt-1">
                              <div className="bg-gray-50 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-[8px] border border-gray-200/60">
                                <code className="text-blue-600 text-[11px] sm:text-[12px] break-all font-medium" style={{ letterSpacing: '-0.01em' }}>
                        {bookingPublicUrl}
                      </code>
                    </div>
                  </div>
                          </details>
                )}
              </div>
              </div>
            </div>
          )}

                {!selectedCategoryForEdit ? (
                  <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
                    <div className="text-center max-w-md">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-[10px] sm:rounded-[12px] bg-gray-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                        <SettingsIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                      </div>
                      <h3 className="text-[18px] sm:text-[20px] font-bold text-gray-900 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                        Create Your Online Booking Flow
                    </h3>
                      <p className="text-[14px] sm:text-[15px] text-gray-600 mb-4 sm:mb-6 px-2" style={{ letterSpacing: '-0.01em' }}>
                        Select a category from the left sidebar to start building your booking flow, or create a new category to get started.
                      </p>
                      {categories.length === 0 && (
                      <button
                          onClick={() => setIsModalOpen(true)}
                          className="inline-flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-3 text-[14px] sm:text-[15px] font-semibold text-white bg-blue-600 rounded-[8px] sm:rounded-[10px] active:bg-blue-700 transition-all duration-150 active:scale-[0.97] touch-manipulation shadow-sm"
                          style={{ 
                            minHeight: '40px',
                            WebkitTapHighlightColor: 'transparent',
                            letterSpacing: '-0.01em'
                          }}
                        >
                          <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                          Create Your First Category
                      </button>
                    )}
                  </div>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {/* Flow Editor Header */}
                    <div className="mb-3 sm:mb-4">
                      <h3 className="text-[15px] sm:text-[17px] font-bold text-gray-900 mb-0.5" style={{ letterSpacing: '-0.01em' }}>
                        {selectedCategoryForEdit.category_title}
                      </h3>
                      <p className="text-[12px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                        Build the flow customers will follow when booking this category
                      </p>
                  </div>
                  
                    {/* Flow Visualization - Step 1: Category Card + Sub-categories */}
                    <div className="w-full flex items-center justify-start p-2 sm:p-4">
                      {/* Category Card - Left Side, Centered to Sub-categories */}
                      <div className="flex-shrink-0 px-3 sm:px-4 py-2 sm:py-2.5 rounded-[8px] sm:rounded-[10px] text-[13px] sm:text-[14px] font-semibold bg-gray-100 text-gray-900 border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]" style={{ letterSpacing: '-0.01em' }}>
                        {selectedCategoryForEdit.category_title}
                      </div>
                      
                      {/* Connector Line */}
                      <div className="h-px w-6 sm:w-8 bg-gray-300 mx-1.5 sm:mx-2 flex-shrink-0"></div>
                      
                      {/* Sub-categories or Add Button */}
                      {selectedCategoryForEdit.sub_categories && selectedCategoryForEdit.sub_categories.length > 0 ? (
                        /* Show Sub-categories with Services - Dynamic spacing */
                        <div className="flex flex-col items-start flex-1">
                          {/* Existing Sub-categories */}
                          {selectedCategoryForEdit.sub_categories.map((subCategory, index) => {
                            // Calculate dynamic spacing: base margin + additional space per service
                            const serviceCount = subCategory.services?.length || 0;
                            const baseMargin = 1.5; // Base 1.5rem (24px) margin between sub-categories
                            const serviceMargin = serviceCount * 0.5; // 0.5rem per service for adequate spacing
                            const totalMargin = baseMargin + serviceMargin;
                            
                            return (
                              <div 
                                key={index} 
                                className="flex items-center space-x-2"
                                style={{ marginBottom: `${totalMargin}rem` }}
                              >
                                {/* Sub-category Card - Fixed width to match longest, Centered to Services */}
                                <div className="relative px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-[8px] sm:rounded-[10px] text-[12px] sm:text-[14px] font-semibold bg-blue-50 text-blue-900 border border-blue-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] w-32 sm:w-40 min-w-[8rem] sm:min-w-[10rem] text-center flex-shrink-0 group" style={{ letterSpacing: '-0.01em' }}>
                        <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSubCategory(subCategory);
                                    }}
                                    className="absolute -top-1.5 -right-1.5 p-1 bg-red-600 text-white rounded-full active:bg-red-700 transition-all duration-150 touch-manipulation opacity-0 group-hover:opacity-100 z-10 shadow-sm"
                                    style={{ 
                                      minHeight: '20px',
                                      minWidth: '20px',
                                      WebkitTapHighlightColor: 'transparent'
                                    }}
                                    title="Delete subcategory"
                                  >
                                    <Trash2 className="h-2.5 w-2.5" />
                        </button>
                                  {subCategory.title}
                                </div>
                                
                                {/* Connector Line */}
                                <div className="h-px w-6 sm:w-8 bg-gray-300 flex-shrink-0"></div>
                                
                                {/* Services Section */}
                                {subCategory.services && subCategory.services.length > 0 ? (
                                  /* When services exist: Services on left, button on right */
                                  <div className="flex items-center space-x-2 flex-shrink-0">
                                    {/* Services Column - Stacked Vertically */}
                                    <div className="flex flex-col space-y-1.5">
                                      {subCategory.services.map((service, serviceIndex) => {
                                        const serviceTag = serviceTags.get(service.service.id);
                                        const hasTag = serviceTag?.title_tag_enabled && serviceTag?.title_tag_text;
                                        const hasMeter = serviceTag?.service_meter_enabled && serviceTag?.meter_title;
                                        
                                        return (
                                          <div key={serviceIndex} className="relative px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-[8px] sm:rounded-[10px] text-[11px] sm:text-[12px] font-semibold bg-yellow-50 text-gray-900 border border-yellow-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] group" style={{ letterSpacing: '-0.01em' }}>
                                            {/* Delete Button */}
                          <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteService(subCategory, service.service);
                                              }}
                                              className="absolute -top-1.5 -right-1.5 p-1 bg-red-600 text-white rounded-full active:bg-red-700 transition-all duration-150 touch-manipulation opacity-0 group-hover:opacity-100 z-10 shadow-sm"
                                              style={{ 
                                                minHeight: '18px',
                                                minWidth: '18px',
                                                WebkitTapHighlightColor: 'transparent'
                                              }}
                                              title="Delete service"
                                            >
                                              <Trash2 className="h-2.5 w-2.5" />
                          </button>
                                            
                                            {/* Tag Plus Icon */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                                handleOpenTagModal(service.service, service.priceOption);
                                              }}
                                              className="absolute -top-1.5 left-1 p-0.5 bg-blue-600 text-white rounded-full active:bg-blue-700 transition-all duration-150 touch-manipulation opacity-0 group-hover:opacity-100 z-10 shadow-sm"
                                              style={{ 
                                                minHeight: '16px',
                                                minWidth: '16px',
                                                WebkitTapHighlightColor: 'transparent'
                                              }}
                                              title="Add tag"
                                            >
                                              <Plus className="h-2.5 w-2.5" />
                                </button>
                                            
                                            {/* Service Name and Tag */}
                                            <div className="mb-0.5 sm:mb-1">
                                              <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5">
                                                <span className="text-[11px] sm:text-[12px] font-semibold text-gray-900 truncate flex-1">{service.service.name} ({service.priceOption.name})</span>
                                                {hasTag && (
                                                  <span className="px-1.5 sm:px-2 py-0.5 bg-red-500 text-white text-[9px] sm:text-[10px] font-medium rounded-full whitespace-nowrap flex-shrink-0">
                                                    {serviceTag.title_tag_text}
                                                  </span>
                                                )}
                                  </div>
                                </div>
                                            
                                            {/* Service Meter */}
                                            {hasMeter && (
                                              <div className="mt-1.5 bg-gray-50 rounded-[6px] p-1.5">
                                                <div className="mb-1">
                                                  <p className="text-[10px] font-medium text-gray-700">{serviceTag.meter_title}</p>
                              </div>
                                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                  <div 
                                                    className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                                    style={{ width: `${((serviceTag.meter_rating || 0) / 10) * 100}%` }}
                                                  ></div>
                          </div>
                        </div>
                  )}
                </div>
                                        );
                                      })}
                        </div>
                                    
                                    {/* Add Service Button - On the right */}
                            <button
                                      onClick={() => {
                                        setSelectedCategoryId(selectedCategoryForEdit.id);
                                        setSelectedSubCategory(subCategory);
                                        setIsSelectServiceModalOpen(true);
                                      }}
                                      className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-[8px] sm:rounded-[10px] text-[12px] sm:text-[13px] font-semibold bg-gray-50 text-gray-700 border-2 border-dashed border-gray-300 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:bg-gray-100 transition-all duration-150 touch-manipulation whitespace-nowrap flex-shrink-0"
                                      style={{ 
                                        letterSpacing: '-0.01em',
                                        WebkitTapHighlightColor: 'transparent'
                                      }}
                                    >
                                      + Add Service
                            </button>
                        </div>
                                ) : (
                                  /* When no services: Button below */
                                  <div className="flex flex-col space-y-1.5 flex-shrink-0">
                                  <button
                                    onClick={() => {
                                      setSelectedCategoryId(selectedCategoryForEdit.id);
                                      setSelectedSubCategory(subCategory);
                                      setIsSelectServiceModalOpen(true);
                                    }}
                                      className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-[8px] sm:rounded-[10px] text-[12px] sm:text-[13px] font-semibold bg-gray-50 text-gray-700 border-2 border-dashed border-gray-300 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:bg-gray-100 transition-all duration-150 touch-manipulation whitespace-nowrap"
                                      style={{ 
                                        letterSpacing: '-0.01em',
                                        WebkitTapHighlightColor: 'transparent'
                                      }}
                                    >
                                      Assign Service
                                            </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          
                          {/* Add Subcategory Box - Below */}
                                            <button 
                            onClick={() => handlePlusClick(selectedCategoryForEdit.id, true)}
                            className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-[8px] sm:rounded-[10px] text-[12px] sm:text-[13px] font-semibold bg-blue-50 text-blue-900 border-2 border-dashed border-blue-300 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:bg-blue-100 transition-all duration-150 touch-manipulation w-32 sm:w-40 min-w-[8rem] sm:min-w-[10rem] text-center"
                            style={{ 
                              letterSpacing: '-0.01em',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                          >
                            + Add Subcategory
                                            </button>
                                        </div>
                      ) : (
                                    <button
                          onClick={() => handlePlusClick(selectedCategoryForEdit.id)}
                          className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-[8px] sm:rounded-[10px] text-[12px] sm:text-[13px] font-semibold bg-blue-50 text-blue-900 border-2 border-dashed border-blue-300 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:bg-blue-100 transition-all duration-150 touch-manipulation"
                          style={{ 
                            letterSpacing: '-0.01em',
                            WebkitTapHighlightColor: 'transparent'
                          }}
                        >
                          + Add Subcategory
                                    </button>
                                )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Booking Page Customization Section */}
          {!embedded && (
          <div className="bg-white rounded-[10px] sm:rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] mb-4 sm:mb-6">
            <div className="p-3 sm:p-4 md:p-6">
              <div className="space-y-2.5 sm:space-y-3">
                {/* Header */}
                <div>
                  <h3 className="text-[15px] sm:text-[17px] font-bold text-gray-900 mb-0.5 sm:mb-1" style={{ letterSpacing: '-0.01em' }}>Page Settings</h3>
                  <p className="text-[12px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>Customize your online booking page appearance and behavior</p>
                </div>
                
                {/* Appearance Settings */}
                <details className="group border border-gray-200/60 rounded-[8px] sm:rounded-[10px] shadow-sm">
                  <summary className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 cursor-pointer text-[14px] sm:text-[15px] font-semibold text-gray-900 rounded-t-[8px] sm:rounded-t-[10px] hover:bg-gray-100 transition-all duration-200 active:scale-[0.99] touch-manipulation [&::-webkit-details-marker]:hidden [&::marker]:hidden flex items-center justify-between" style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}>
                    <span>Appearance</span>
                    <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 bg-white rounded-b-[8px] sm:rounded-b-[10px]">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {/* Logo Upload */}
                      <div>
                        <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>Logo</label>
                        <BusinessLogoUpload size="small" />
                      </div>

                        {/* Navigation Color */}
                        <div>
                        <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                            Navigation Bar Color
                          </label>
                          <div className="flex items-center space-x-2">
                            {/* Mobile: Color button that opens modal */}
                            <button
                              onClick={() => openColorPicker('navbar', navbarColor)}
                            className="sm:hidden w-9 h-9 rounded-[8px] border border-gray-300 flex items-center justify-center"
                              style={{ backgroundColor: navbarColor }}
                            >
                              <span className="sr-only">Select navbar color</span>
                            </button>
                            
                            {/* Desktop: Native color picker */}
                            <input
                              type="color"
                              value={navbarColor}
                              onChange={(e) => setNavbarColor(e.target.value)}
                            className="hidden sm:block w-8 h-8 rounded-[8px] border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={navbarColor}
                              onChange={(e) => setNavbarColor(e.target.value)}
                            className="hidden sm:flex flex-1 px-2.5 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="#000000"
                            style={{ letterSpacing: '-0.01em' }}
                            />
                            <button
                              onClick={handleSaveNavbarColor}
                            className="hidden sm:inline-flex items-center px-2.5 sm:px-3 py-1.5 sm:py-2 text-[12px] sm:text-[13px] font-semibold text-white bg-blue-600 rounded-[8px] active:bg-blue-700 transition-all duration-150 active:scale-[0.97] touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                            >
                              Save
                            </button>
                          </div>
                        <p className="text-[11px] sm:text-[12px] text-gray-500 mt-1 sm:mt-1.5" style={{ letterSpacing: '-0.01em' }}>
                          Color for the top navigation bar
                          </p>
                        </div>
                      </div>
                      
                      {/* Logo Style Setting */}
                      {selectedBusiness?.booking_settings?.logo_url && (
                      <div>
                        <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                            Logo Style
                          </label>
                          <button
                            onClick={() => {
                              console.log('Logo invert button clicked. Current value:', logoInvert);
                              const newValue = !logoInvert;
                              console.log('Setting new value:', newValue);
                              setLogoInvert(newValue);
                              handleSaveLogoInvert(newValue);
                            }}
                          className={`w-full px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-[8px] border text-[12px] sm:text-[13px] font-semibold transition-all duration-150 active:scale-[0.97] touch-manipulation ${
                              logoInvert 
                              ? 'bg-blue-100 border-blue-300 text-blue-800' 
                                : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                            }`}
                          style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                          >
                            {logoInvert ? 'White Logo (for dark backgrounds)' : 'Original Colors'}
                          </button>
                        <p className="text-[11px] sm:text-[12px] text-gray-500 mt-1 sm:mt-1.5" style={{ letterSpacing: '-0.01em' }}>
                            Make your logo white when using dark navigation bar colors
                          </p>
                        </div>
                      )}
                  </div>
                </details>

                {/* Header Settings */}
                <details className="group border border-gray-200/60 rounded-[8px] sm:rounded-[10px] shadow-sm">
                  <summary className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 cursor-pointer text-[14px] sm:text-[15px] font-semibold text-gray-900 rounded-t-[8px] sm:rounded-t-[10px] hover:bg-gray-100 transition-all duration-200 active:scale-[0.99] touch-manipulation [&::-webkit-details-marker]:hidden [&::marker]:hidden flex items-center justify-between" style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}>
                    <span>Header</span>
                    <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="p-3 sm:p-4 bg-white rounded-b-[8px] sm:rounded-b-[10px]">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 pr-3">
                        <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-0.5 sm:mb-1" style={{ letterSpacing: '-0.01em' }}>
                          Call Button
                          </label>
                        <p className="text-[11px] sm:text-[12px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                          Display a call button in the header for easy contact
                        </p>
                      </div>
                          <Switch
                            checked={popupSettings.call_button_enabled}
                        onChange={(enabled) => handlePopupSettingChange('call_button_enabled', enabled as boolean)}
                            disabled={!popupSettingsLoaded}
                            className={`${
                          popupSettings.call_button_enabled ? 'bg-blue-600' : 'bg-gray-200'
                        } relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex-shrink-0`}
                          >
                            <span
                              className={`${
                            popupSettings.call_button_enabled ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                          } inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </Switch>
                        </div>
                      </div>
                </details>

                {/* Booking Settings */}
                <details className="group border border-gray-200/60 rounded-[8px] sm:rounded-[10px] shadow-sm">
                  <summary className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 cursor-pointer text-[14px] sm:text-[15px] font-semibold text-gray-900 rounded-t-[8px] sm:rounded-t-[10px] hover:bg-gray-100 transition-all duration-200 active:scale-[0.99] touch-manipulation [&::-webkit-details-marker]:hidden [&::marker]:hidden flex items-center justify-between" style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}>
                    <span>Booking</span>
                    <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="p-3 sm:p-4 bg-white rounded-b-[8px] sm:rounded-b-[10px]">
                    {/* Custom Thank You URL */}
                    <div>
                      <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                        Custom Thank You Page URL
                      </label>
                      <input
                        type="url"
                        value={customThankYouUrl}
                        onChange={(e) => setCustomThankYouUrl(e.target.value)}
                        onBlur={(e) => handleCustomThankYouUrlChange(e.target.value)}
                        placeholder="https://yourwebsite.com/thank-you"
                        className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                        style={{ letterSpacing: '-0.01em' }}
                      />
                      <p className="text-[11px] sm:text-[12px] text-gray-500 mt-1 sm:mt-1.5" style={{ letterSpacing: '-0.01em' }}>
                        {customThankYouUrl 
                          ? 'Customers will be redirected to this URL after booking' 
                          : 'Leave empty to show the default thank you page'}
                          </p>
                        </div>
                  </div>
                </details>

                {/* Promotions Settings */}
                <details className="group border border-gray-200/60 rounded-[8px] sm:rounded-[10px] shadow-sm">
                  <summary className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 cursor-pointer text-[14px] sm:text-[15px] font-semibold text-gray-900 rounded-t-[8px] sm:rounded-t-[10px] hover:bg-gray-100 transition-all duration-200 active:scale-[0.99] touch-manipulation [&::-webkit-details-marker]:hidden [&::marker]:hidden flex items-center justify-between" style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}>
                    <span className="truncate">Promotions</span>
                    <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 transition-transform group-open:rotate-180 flex-shrink-0 ml-1" />
                  </summary>
                  <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 bg-white rounded-b-[8px] sm:rounded-b-[10px]">
                    
                    {/* Booking Discount Section */}
                    <div className="p-3 sm:p-4 border border-gray-200/60 rounded-[8px] bg-gradient-to-br from-blue-50 to-indigo-50">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-[13px] sm:text-[14px] font-bold text-gray-900 mb-1" style={{ letterSpacing: '-0.01em' }}>
                            Booking Discount
                          </label>
                          <p className="text-[11px] sm:text-[12px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                            Apply an overall discount to all services in online bookings
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                            Discount Percentage
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={popupSettings.booking_discount_percentage}
                              onChange={(e) => handlePopupSettingChange('booking_discount_percentage', parseFloat(e.target.value) || 0)}
                              disabled={!popupSettingsLoaded}
                              className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm disabled:opacity-50"
                              placeholder="0"
                              style={{ letterSpacing: '-0.01em' }}
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] sm:text-[13px] text-gray-500 font-medium">%</span>
                          </div>
                          <p className="text-[11px] sm:text-[12px] text-gray-600 mt-1 sm:mt-1.5 flex items-start" style={{ letterSpacing: '-0.01em' }}>
                            <span className="text-amber-600 font-medium mr-1">⚠️</span>
                            <span>Applies to all services. Set to 0 to disable discount.</span>
                          </p>
                        </div>
                        
                        {popupSettings.booking_discount_percentage > 0 && (
                          <div className="p-2 sm:p-3 bg-white border border-blue-200 rounded-[8px]">
                            <p className="text-[11px] sm:text-[12px] text-blue-800 font-medium" style={{ letterSpacing: '-0.01em' }}>
                              ✓ All services will show {popupSettings.booking_discount_percentage}% off on the booking page
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Google Reviews Badge Section */}
                    <div className="p-3 sm:p-4 border border-gray-200/60 rounded-[8px]">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex-1 pr-3">
                          <label className="block text-[13px] sm:text-[14px] font-bold text-gray-900 mb-0.5 sm:mb-1" style={{ letterSpacing: '-0.01em' }}>
                            Google Reviews Badge
                          </label>
                          <p className="text-[11px] sm:text-[12px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                            Show your star rating on the booking page's address step
                          </p>
                        </div>
                        <Switch
                          checked={popupSettings.google_reviews_badge_enabled}
                          onChange={(enabled: boolean) => handlePopupSettingChange('google_reviews_badge_enabled', enabled)}
                          disabled={!popupSettingsLoaded}
                          className={`${
                            popupSettings.google_reviews_badge_enabled ? 'bg-blue-600' : 'bg-gray-200'
                          } relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex-shrink-0`}
                        >
                          <span
                            className={`${
                              popupSettings.google_reviews_badge_enabled ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                            } inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                      </div>

                      {popupSettings.google_reviews_badge_enabled && popupSettingsLoaded && (
                        <div className="space-y-3 sm:space-y-4 pt-3 border-t border-gray-200/60">
                          <div>
                            <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                              Star Rating (out of 5)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="5"
                              step="0.1"
                              value={popupSettings.google_reviews_rating}
                              onChange={(e) => {
                                const v = Math.min(5, Math.max(0, parseFloat(e.target.value) || 0));
                                handlePopupSettingChange('google_reviews_rating', v);
                              }}
                              className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                              placeholder="4.9"
                              style={{ letterSpacing: '-0.01em' }}
                            />
                          </div>

                          <div>
                            <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                              Review Count <span className="font-normal text-gray-400">(optional)</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={popupSettings.google_reviews_count ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value.trim();
                                handlePopupSettingChange('google_reviews_count', raw === '' ? null : Math.max(0, parseInt(raw, 10) || 0));
                              }}
                              className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                              placeholder="100"
                              style={{ letterSpacing: '-0.01em' }}
                            />
                            <p className="text-[11px] sm:text-[12px] text-gray-500 mt-1 sm:mt-1.5" style={{ letterSpacing: '-0.01em' }}>
                              Shown as "based on {popupSettings.google_reviews_count || 100}+ reviews" — leave empty to hide
                            </p>
                          </div>

                          <div>
                            <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                              Google Reviews Link <span className="font-normal text-gray-400">(optional)</span>
                            </label>
                            <input
                              type="url"
                              value={popupSettings.google_reviews_url}
                              onChange={(e) => setPopupSettings(prev => ({ ...prev, google_reviews_url: e.target.value }))}
                              onBlur={(e) => handlePopupSettingChange('google_reviews_url', e.target.value.trim())}
                              className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                              placeholder="https://g.page/r/..."
                              style={{ letterSpacing: '-0.01em' }}
                            />
                            <p className="text-[11px] sm:text-[12px] text-gray-500 mt-1 sm:mt-1.5" style={{ letterSpacing: '-0.01em' }}>
                              When set, tapping the badge opens your Google reviews
                            </p>
                          </div>

                          {/* Live preview */}
                          <div className="p-2.5 sm:p-3 bg-gray-50 border border-gray-200/60 rounded-[8px]">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Preview</p>
                            <div className="flex items-center justify-center gap-1.5">
                              <svg width={15} height={15} viewBox="0 0 48 48" aria-hidden="true">
                                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                              </svg>
                              <span className="text-amber-400 text-[14px] leading-none tracking-tight">
                                {'★'.repeat(Math.round(Math.min(5, Math.max(0, popupSettings.google_reviews_rating))))}{'☆'.repeat(5 - Math.round(Math.min(5, Math.max(0, popupSettings.google_reviews_rating))))}
                              </span>
                              <span className="text-[12px] font-semibold text-gray-900">{popupSettings.google_reviews_rating}</span>
                              {popupSettings.google_reviews_count != null && popupSettings.google_reviews_count > 0 && (
                                <span className="text-[12px] text-gray-500">· based on {popupSettings.google_reviews_count}+ reviews</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Promotional Popup Section */}
                    <div className="p-3 sm:p-4 border border-gray-200/60 rounded-[8px]">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex-1 pr-3">
                          <label className="block text-[13px] sm:text-[14px] font-bold text-gray-900 mb-0.5 sm:mb-1" style={{ letterSpacing: '-0.01em' }}>
                            Promotional Popup
                          </label>
                          <p className="text-[11px] sm:text-[12px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                            Show a promotional message to visitors
                          </p>
                        </div>
                        <Switch
                          checked={popupSettings.popup_enabled}
                          onChange={handlePopupToggle}
                          disabled={!popupSettingsLoaded}
                          className={`${
                            popupSettings.popup_enabled ? 'bg-blue-600' : 'bg-gray-200'
                          } relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex-shrink-0`}
                        >
                          <span
                            className={`${
                              popupSettings.popup_enabled ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                            } inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                      </div>
                      
                      {popupSettings.popup_enabled && popupSettingsLoaded && (
                        <div className="space-y-3 sm:space-y-4 pt-3 border-t border-gray-200/60">
                      {/* Popup Message */}
                      <div>
                        <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                          Message
                        </label>
                        <textarea
                          value={popupSettings.popup_text}
                          onChange={(e) => handlePopupSettingChange('popup_text', e.target.value)}
                          rows={3}
                          maxLength={200}
                          className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm resize-none"
                          placeholder="Enter your promotional message..."
                          style={{ letterSpacing: '-0.01em' }}
                        />
                        <p className="text-[11px] sm:text-[12px] text-gray-500 mt-1 sm:mt-1.5" style={{ letterSpacing: '-0.01em' }}>
                          Max 200 characters
                        </p>
                      </div>

                      {/* Timing Settings */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                              <div>
                          <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                            Delay (seconds)
                                </label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="60"
                                    value={popupSettings.popup_delay_seconds}
                                    onChange={(e) => handlePopupSettingChange('popup_delay_seconds', parseInt(e.target.value) || 0)}
                            className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                            style={{ letterSpacing: '-0.01em' }}
                                  />
                          <p className="text-[11px] sm:text-[12px] text-gray-500 mt-1 sm:mt-1.5" style={{ letterSpacing: '-0.01em' }}>
                            Time before showing (0-60s)
                                </p>
                              </div>

                              <div>
                          <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                            Duration (seconds)
                                </label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="30"
                                    value={popupSettings.popup_duration_seconds}
                                    onChange={(e) => handlePopupSettingChange('popup_duration_seconds', parseInt(e.target.value) || 5)}
                            className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                            style={{ letterSpacing: '-0.01em' }}
                                  />
                          <p className="text-[11px] sm:text-[12px] text-gray-500 mt-1 sm:mt-1.5" style={{ letterSpacing: '-0.01em' }}>
                            How long it stays visible (1-30s)
                                </p>
                              </div>
                              </div>

                      {/* Button Settings */}
                              <div>
                        <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                          Button Text
                                </label>
                                  <input
                                    type="text"
                                    value={popupSettings.popup_button_text}
                                    onChange={(e) => handlePopupSettingChange('popup_button_text', e.target.value)}
                                    maxLength={50}
                          className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                    placeholder="Enter button text..."
                          style={{ letterSpacing: '-0.01em' }}
                                  />
                        <p className="text-[11px] sm:text-[12px] text-gray-500 mt-1 sm:mt-1.5" style={{ letterSpacing: '-0.01em' }}>
                          Max 50 characters
                                </p>
                              </div>

                      {/* Button Action */}
                              <div>
                        <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                          Button Action
                                </label>
                                  <select
                                    value={popupSettings.popup_button_action}
                                    onChange={(e) => handlePopupSettingChange('popup_button_action', e.target.value)}
                          className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                          style={{ letterSpacing: '-0.01em' }}
                                  >
                                    <option value="url">Redirect to URL</option>
                                    <option value="lead_form">Show lead capture form</option>
                                  </select>
                        <p className="text-[11px] sm:text-[12px] text-gray-500 mt-1 sm:mt-1.5" style={{ letterSpacing: '-0.01em' }}>
                                  What happens when customers click the button
                                </p>
                              </div>

                              {/* Button URL - Only show when action is URL */}
                              {popupSettings.popup_button_action === 'url' && (
                                <div>
                          <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                                    Button URL
                                  </label>
                                    <input
                                      type="url"
                                      value={popupSettings.popup_button_url}
                                      onChange={(e) => handlePopupSettingChange('popup_button_url', e.target.value)}
                            className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                      placeholder="https://example.com"
                            style={{ letterSpacing: '-0.01em' }}
                                    />
                                </div>
                              )}

                      {/* Lead Form Settings - Only show when action is lead_form */}
                              {popupSettings.popup_button_action === 'lead_form' && (
                        <div className="space-y-3 sm:space-y-4 pt-2 border-t border-gray-200/60">
                                  <div className="flex items-center justify-between">
                            <div className="flex-1 pr-3">
                              <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-0.5 sm:mb-1" style={{ letterSpacing: '-0.01em' }}>
                                Send Email on Submit
                                    </label>
                              <p className="text-[11px] sm:text-[12px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                                Automatically send an email when form is submitted
                              </p>
                            </div>
                                    <Switch
                                      checked={popupSettings.popup_send_email}
                                      onChange={(enabled) => handlePopupSettingChange('popup_send_email', enabled ? 'true' : 'false')}
                                      className={`${
                                popupSettings.popup_send_email ? 'bg-blue-600' : 'bg-gray-200'
                              } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex-shrink-0`}
                                    >
                                      <span
                                        className={`${
                                          popupSettings.popup_send_email ? 'translate-x-5' : 'translate-x-1'
                                        } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                                      />
                                    </Switch>
                                  </div>

                                  {/* Email template selection - Only show when send email is enabled */}
                                  {popupSettings.popup_send_email && (
                                    <div>
                              <label className="block text-[12px] sm:text-[13px] font-semibold text-gray-700 mb-1.5 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                                Email Template
                                      </label>
                                        <select
                                          value={popupSettings.popup_email_template_id}
                                          onChange={(e) => handlePopupSettingChange('popup_email_template_id', e.target.value)}
                                className="block w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-[8px] text-[12px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                style={{ letterSpacing: '-0.01em' }}
                                        >
                                          <option value="">Select a template</option>
                                          {emailTemplates.map((template) => (
                                            <option key={template.id} value={template.id}>
                                              {template.name}
                                            </option>
                                          ))}
                                        </select>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>
          )}

      {/* Create Category Modal */}
      {isModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[999999] p-2 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-2xl w-full max-w-md sm:mx-4 shadow-xl border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-5 bg-gray-50 rounded-t-xl sm:rounded-t-2xl">
              <h3 className="text-base sm:text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Create Category</h3>
              <button 
                onClick={() => setIsModalOpen(false)} 
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="w-4 h-4 sm:w-4 sm:h-4" />
              </button>
            </div>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveCategory();
              }}
              className="px-3 sm:px-6 py-4 sm:py-6"
            >
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={categoryTitle}
                  onChange={(e) => setCategoryTitle(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all duration-200"
                  placeholder="Enter category title"
                  required
                  autoFocus
                  style={{ 
                    minHeight: '44px',
                    fontSize: '16px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:w-auto px-3 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 active:scale-95 shadow-sm touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!categoryTitle.trim() || isLoading}
                  className="w-full sm:w-auto px-3 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 active:scale-95 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  {isLoading ? 'Creating...' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      <Dialog
        open={isAddPortalModalOpen}
        onClose={() => {
          if (!isCreatingPortal) setIsAddPortalModalOpen(false);
        }}
        className="relative z-[999999]"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-gray-900 mb-1">
              Add another online booking
            </Dialog.Title>
            <p className="text-sm text-gray-500 mb-4">
              This creates a new booking flow with its own public link. The URL suffix is appended after{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">/book/</code>
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">URL suffix</label>
                <input
                  value={newPortalSlug}
                  onChange={(e) => setNewPortalSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="e.g. commercial"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  disabled={isCreatingPortal}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Display name (optional)</label>
                <input
                  value={newPortalDisplayName}
                  onChange={(e) => setNewPortalDisplayName(e.target.value)}
                  placeholder="Label for your team"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  disabled={isCreatingPortal}
                />
              </div>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setIsAddPortalModalOpen(false)}
                disabled={isCreatingPortal}
                className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleCreateAdditionalPortal()}
                disabled={isCreatingPortal}
                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isCreatingPortal ? 'Creating…' : 'Create'}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Options Modal */}
      <Dialog
        open={isOptionsModalOpen}
        onClose={() => setIsOptionsModalOpen(false)}
        className="relative z-[999999]"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm rounded-lg bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Add Sub-category
              </Dialog.Title>
              <button
                onClick={() => setIsOptionsModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setIsOptionsModalOpen(false);
                  setIsSubCategoryModalOpen(true);
                }}
                className="w-full px-4 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-100 rounded-md"
              >
                Create Sub Categories
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Sub-category Modal */}
      <Dialog
        open={isSubCategoryModalOpen}
        onClose={() => setIsSubCategoryModalOpen(false)}
        className="relative z-[999999]"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Add Sub-category
              </Dialog.Title>
              <button
                onClick={() => setIsSubCategoryModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4">
              <label htmlFor="subCategoryTitle" className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                type="text"
                id="subCategoryTitle"
                value={subCategoryTitle}
                onChange={(e) => setSubCategoryTitle(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Enter sub-category title"
              />
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setIsSubCategoryModalOpen(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSubCategory}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Add
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Select Service Modal */}
      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => {
          setIsSelectServiceModalOpen(false);
          setSelectedSubCategory(null);
        }}
        onAdd={handleServiceAdd}
      />

      {/* Color Picker Modal */}
      <Dialog
        open={isColorPickerOpen}
        onClose={() => setIsColorPickerOpen(false)}
        className="relative z-[999999]"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Choose Navigation Bar Color
              </Dialog.Title>
              <button
                onClick={() => setIsColorPickerOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4">
              <div className="flex flex-col space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Select a color
                </label>
                <input
                  type="color"
                  value={navbarColor}
                  onChange={(e) => setNavbarColor(e.target.value)}
                  className="w-full h-12 p-1 rounded cursor-pointer"
                />
                
                <div className="flex items-center space-x-2 mt-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Color code
                    </label>
                    <input
                      type="text"
                      value={navbarColor}
                      onChange={(e) => {
                        // Validate hex color format
                        const isValidHex = /^#([0-9A-F]{3}){1,2}$/i.test(e.target.value);
                        if (isValidHex || e.target.value.startsWith('#')) {
                          setNavbarColor(e.target.value);
                        }
                      }}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="w-12 h-12 rounded border border-gray-300" style={{ backgroundColor: navbarColor }}></div>
                </div>
                
                <div className="mt-2">
                  <p className="text-sm text-gray-500">Preview:</p>
                  <div className="mt-2 w-full h-12 rounded-t-lg" style={{ backgroundColor: navbarColor }}></div>
                  <div className="bg-white border border-gray-300 border-t-0 h-12 rounded-b-lg flex items-center justify-center text-sm text-gray-700">
                    Booking Page Preview
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setIsColorPickerOpen(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNavbarColor}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save Color
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Service Info Modal */}
      <Dialog
        open={isServiceInfoModalOpen}
        onClose={() => setIsServiceInfoModalOpen(false)}
        className="relative z-[999999]"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900 truncate max-w-xs">
                {selectedServiceInfo?.service.name || "Service Details"}
              </Dialog.Title>
              <button
                onClick={() => setIsServiceInfoModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {selectedServiceInfo && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Service Name</h3>
                  <p className="mt-1 text-sm text-gray-900">{selectedServiceInfo.service.name}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Description</h3>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedServiceInfo.service.description || "No description available"}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Price Option</h3>
                    <p className="mt-1 text-sm text-gray-900">{selectedServiceInfo.priceOption.name}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Price</h3>
                    <p className="mt-1 text-sm text-gray-900">${selectedServiceInfo.priceOption.price.toFixed(2)}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Duration</h3>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedServiceInfo.priceOption.duration_minutes >= 60 
                        ? `${Math.floor(selectedServiceInfo.priceOption.duration_minutes / 60)} hour${selectedServiceInfo.priceOption.duration_minutes >= 120 ? 's' : ''} ${selectedServiceInfo.priceOption.duration_minutes % 60 > 0 ? `${selectedServiceInfo.priceOption.duration_minutes % 60} min` : ''}`
                        : `${selectedServiceInfo.priceOption.duration_minutes} min`
                      }
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Tax Exempt</h3>
                    <p className="mt-1 text-sm text-gray-900">{selectedServiceInfo.service.tax_exempt ? "Yes" : "No"}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setIsServiceInfoModalOpen(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Color Picker Modal */}
      {colorPickerModal.isOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={closeColorPicker}
        >
          <div 
            className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Choose {colorPickerModal.type} Color
                </h3>
                <button
                  onClick={closeColorPicker}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="grid grid-cols-7 gap-2 mb-4">
                {colorPalette.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleColorSelect(color)}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${
                      colorPickerModal.currentColor === color 
                        ? 'border-blue-500 scale-110' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  >
                    {colorPickerModal.currentColor === color && (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full shadow-sm"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={colorPickerModal.currentColor}
                    onChange={(e) => setColorPickerModal(prev => ({
                      ...prev,
                      currentColor: e.target.value
                    }))}
                    className="w-10 h-10 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={colorPickerModal.currentColor}
                    onChange={(e) => setColorPickerModal(prev => ({
                      ...prev,
                      currentColor: e.target.value
                    }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="#000000"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={closeColorPicker}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleColorSelect(colorPickerModal.currentColor)}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
        </div>
      </main>

      {/* Modifier Modal */}
      <ModifierModal
        isOpen={isModifierModalOpen}
        onClose={handleCloseModifierModal}
        service={selectedServiceForModifier}
      />

      {/* Service Tag Modal */}
      {isServiceTagModalOpen && selectedServiceForTag && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[999999] p-2 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-2xl w-full max-w-md sm:mx-4 shadow-xl border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-5 bg-gray-50 rounded-t-xl sm:rounded-t-2xl">
              <h3 className="text-base sm:text-xl font-bold text-gray-900">Add Service Tag</h3>
              <button 
                onClick={() => {
                  setIsServiceTagModalOpen(false);
                  setSelectedServiceForTag(null);
                  setServiceTagText('');
                  setServiceTagEnabled(false);
                  setServiceMeterEnabled(false);
                  setServiceMeterTitle('');
                  setServiceMeterRating(0);
                }} 
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
              >
                <X className="w-4 h-4 sm:w-4 sm:h-4" />
              </button>
            </div>

            <div className="px-3 sm:px-6 py-4 sm:py-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Service: <span className="font-semibold text-gray-900">{selectedServiceForTag.service.name}</span>
                </p>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Enable Tag
                  </label>
                  <Switch
                    checked={serviceTagEnabled}
                    onChange={setServiceTagEnabled}
                    className={`${
                      serviceTagEnabled ? 'bg-blue-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                  >
                    <span
                      className={`${
                        serviceTagEnabled ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>
              </div>

              {serviceTagEnabled && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tag Text (e.g., "Best Deal", "Popular", etc.)
                  </label>
                  <input
                    type="text"
                    value={serviceTagText}
                    onChange={(e) => setServiceTagText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                    placeholder="Enter tag text..."
                    maxLength={50}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This tag will appear on the service card in the booking page
                  </p>
                </div>
              )}

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Enable Quality Meter
                  </label>
                  <Switch
                    checked={serviceMeterEnabled}
                    onChange={setServiceMeterEnabled}
                    className={`${
                      serviceMeterEnabled ? 'bg-blue-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                  >
                    <span
                      className={`${
                        serviceMeterEnabled ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>
              </div>

              {serviceMeterEnabled && (
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Meter Title (e.g., "Quality", "Customer Rating", etc.)
                    </label>
                    <input
                      type="text"
                      value={serviceMeterTitle}
                      onChange={(e) => setServiceMeterTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                      placeholder="Enter meter title..."
                      maxLength={50}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Rating (0-10)
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="0.5"
                        value={serviceMeterRating}
                        onChange={(e) => setServiceMeterRating(parseFloat(e.target.value))}
                        className="flex-1"
                      />
                      <span className="text-sm font-semibold text-gray-700 w-12 text-right">
                        {serviceMeterRating.toFixed(1)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      This meter will appear on the service card in the booking page
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsServiceTagModalOpen(false);
                    setSelectedServiceForTag(null);
                    setServiceTagText('');
                    setServiceTagEnabled(false);
                    setServiceMeterEnabled(false);
                    setServiceMeterTitle('');
                    setServiceMeterRating(0);
                  }}
                  className="w-full sm:w-auto px-3 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 active:scale-95 shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveServiceTag}
                  disabled={(serviceTagEnabled && !serviceTagText.trim()) || (serviceMeterEnabled && (!serviceMeterTitle.trim() || serviceMeterRating === 0))}
                  className="w-full sm:w-auto px-3 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 active:scale-95 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Tag & Meter
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Category Confirmation Modal */}
      {isDeleteCategoryModalOpen && categoryToDelete && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[999999] p-2 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-2xl w-full max-w-md sm:mx-4 shadow-xl border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-5 bg-gray-50 rounded-t-xl sm:rounded-t-2xl">
              <h3 className="text-base sm:text-xl font-bold text-gray-900">Delete Category</h3>
              <button 
                onClick={() => {
                  setIsDeleteCategoryModalOpen(false);
                  setCategoryToDelete(null);
                }} 
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
              >
                <X className="w-4 h-4 sm:w-4 sm:h-4" />
              </button>
            </div>

            <div className="px-3 sm:px-6 py-4 sm:py-6">
              <div className="mb-4">
                <p className="text-sm sm:text-base text-gray-700 mb-2">
                  Are you sure you want to delete the category <span className="font-semibold text-gray-900">"{categoryToDelete.title}"</span>?
                </p>
                <p className="text-xs sm:text-sm text-gray-500">
                  This action cannot be undone. All sub-categories and services associated with this category will also be removed.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsDeleteCategoryModalOpen(false);
                    setCategoryToDelete(null);
                  }}
                  className="w-full sm:w-auto px-3 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 active:scale-95 shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDeleteCategory}
                  className="w-full sm:w-auto px-3 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-all duration-200 active:scale-95 shadow-lg"
                >
                  Delete Category
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
} 